<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="loading && !report" kind="loading"></report-state>
    <report-state v-else-if="!rows.length" icon="mdi-account-off-outline" :title="emptyText"></report-state>

    <ui-table
      v-else
      variant="report"
      wrap-class="rd-card--scroll"
      table-class="rd-tbl--workers"
      :columns="columns"
      :rows="rows"
      :row-key="(row) => row.key"
      :sort="sort"
      :totals="sums"
      :total-label="$t('total')"
      :selected="isOpen"
      :row-class="(row) => ({ 'is-idle': row.state === 'off' })"
      @sort="sortBy"
      @open="openWorker"
    >
      <template #head-shift="{ column }">
        <report-shift-tag :shift="column.shift.id"></report-shift-tag>
      </template>

      <template #cell-name="{ row }">
        <span class="rd-person">
          <span class="ui-avatar ui-avatar--neutral ui-avatar--sm">{{ row.initials }}</span>
          <span class="rd-person__name">{{ row.name }}</span>
        </span>
      </template>

      <template #cell-shift="{ row, column }">
        <span v-if="row[column.key]" class="rs-tag rs-tag--count" :data-shift="column.shift.id">
          {{ row[column.key] }}
          <span class="rs-tag__sr">{{ $t("report_mobile.shift_n", { n: column.shift.id }) }}</span>
        </span>
        <template v-else>
          <span class="rd-dash" aria-hidden="true">–</span>
          <span class="rd-visually-hidden">0</span>
        </template>
      </template>

      <template #cell-status="{ row }">
        <span class="rd-status" :class="`rd-status--${row.state}`">{{ row.statusLabel }}</span>
      </template>
    </ui-table>
  </div>
</template>

<script>
import CompanyMultiRestaurantTabEmployees from "@/components/company/reports/multi-restaurant-tabs/CompanyMultiRestaurantTabEmployees.vue";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import hasMoreEmployeeRows from "@/components/report/shared/more/hasMoreEmployeeRows";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportDesktopWorkerMonth from "@/components/report/desktop/screens/ReportDesktopWorkerMonth.vue";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import hasMultiFeed from "../../../hasMultiFeed";

/** Radnici koje računa stari tab (po izabranim restoranima i periodu), bez filtera statusa. */
const baseUsers = CompanyMultiRestaurantTabEmployees.computed.users;

/** Radnik ima narudžbu / preuzimanje: isti uslovi kao u starom filteru statusa (0 svi, 1–4 kao u listi). */
const matchesStatus = (user, status) => {
  const ordered = (user.order_count || 0) > 0;
  const taken = (user.order_payed || 0) > 0;
  switch (status) {
    case 1:
      return ordered;
    case 2:
      return !ordered;
    case 3:
      return ordered && taken;
    case 4:
      return ordered && !taken;
    default:
      return true;
  }
};

/**
 * Radnici firme "više restorana" na računaru: poručeno / preuzeto / razlika po radniku sa smenama u kojima je
 * poručio i statusom. Filter statusa su čipovi sa brojevima; klik na red otvara mesečni pregled radnika (kod svih
 * restorana) u panelu. Restorane, smene, period i pretragu čita iz zajedničke trake. Nasleđuje učitavanje, štampu i
 * Excel iz starog taba (filter statusa je ovde, pa štampa i Excel prate ono što je na ekranu).
 */
export default {
  name: "CompanyDesktopMultiEmployees",
  extends: CompanyMultiRestaurantTabEmployees,
  mixins: [hasMultiFeed, hasMoreEmployeeRows, hasTableSort],
  components: { ReportShiftTag, ReportState, ReportTools },
  inject: {
    shell: { from: "reportShell", default: null },
  },
  data: () => ({
    sort: { key: "name", dir: 1 },
  }),
  computed: {
    /** Stari tab ne filtrira po statusu: filter (statusFilter) primenjuje ovaj ekran. */
    orderStatus: {
      get() {
        return 0;
      },
      set() {},
    },
    /** Status porudžbe je u zajedničkoj traci ljuske (računar) i u čipovima (telefon), pa je izbor u store-u. */
    statusFilter: {
      get() {
        return this.$store.state.report.filters.employeeStatus;
      },
      set(value) {
        this.$store.commit("report/SET_FILTER", { key: "employeeStatus", value });
      },
    },
    /** Kolone tabele: iste služe za zaglavlje, redove i zbirni red. */
    columns() {
      return [
        { key: "id", label: "ID", cls: "rd-col-id", num: true, muted: true, sortable: true },
        { key: "name", label: this.$t("worker"), name: true, sortable: true },
        ...(this.hasBranches
          ? [{ key: "companyName", label: this.$t("branch"), muted: true, sortable: true, value: (row) => row.companyName || "–", title: (row) => row.companyName }]
          : []),
        ...(this.hasExternalCode
          ? [{ key: "externalCode", label: this.$t("external_code"), num: true, muted: true, value: (row) => row.externalCode || "–" }]
          : []),
        ...(this.isSingleDay
          ? [
              {
                key: "time",
                label: this.$t("time"),
                cls: "rd-col-time",
                headTitle: this.$t("order_datetime"),
                num: true,
                muted: true,
                value: (row) => row.time || "–",
              },
            ]
          : []),
        ...this.visibleShifts.map((shift) => ({ key: shift.key, slot: "shift", shift, align: "center" })),
        { key: "ordered", label: this.$t("ordered"), align: "end", num: true, zero: true, sortable: true },
        { key: "taken", label: this.$t("taken"), cls: "rd-col-taken", align: "end", num: true, zero: true, sortable: true },
        {
          key: "difference",
          label: this.$t("difference"),
          align: "end",
          num: true,
          zero: true,
          sortable: true,
          cellClass: (row) => ({ "is-alert": row.difference > 0 }),
        },
        { key: "status", label: this.$t("status") },
        { key: "go", label: "", go: true },
      ];
    },
    filters() {
      return this.$store.state.report.filters;
    },
    busy() {
      return this.loading;
    },
    hasRows() {
      return this.rows.length > 0;
    },
    /** Svi radnici (stari tab ne filtrira po statusu dok je njegov orderStatus 0). */
    allUsers() {
      return baseUsers.call(this);
    },
    users() {
      return this.allUsers.filter((user) => matchesStatus(user, this.statusFilter));
    },
    statusChips() {
      const users = this.allUsers;
      const counts = [0, 1, 2, 3, 4].reduce((acc, status) => {
        acc[status] = users.filter((user) => matchesStatus(user, status)).length;
        return acc;
      }, {});
      return this.statusItems.map((item) => ({ value: item.value, label: item.text, count: counts[item.value] }));
    },
    rows() {
      return this.sortRows(this.matchedRows);
    },
    emptyText() {
      if (this.filters.search) {
        return this.$t("report_mobile.no_results_for", { search: this.filters.search });
      }
      return this.allUsers.length ? this.$t("report_mobile.no_workers_in_view") : this.$t("no_employee_report_data");
    },
    kpis() {
      return [
        { key: "ordered", label: this.$t("ordered"), value: this.sums.ordered, tone: "inverse" },
        { key: "taken", label: this.$t("taken"), value: this.sums.taken },
        { key: "difference", label: this.$t("difference"), value: this.sums.difference },
      ];
    },
  },
  watch: {
    /** Opcije statusa za traku ljuske (računar). */
    statusChips: {
      handler(chips) {
        this.$emit("status-options", chips);
      },
      immediate: true,
    },
  },
  created() {
    // ekran uvek počinje sa svim radnicima (kao kad je izbor bio u samom ekranu)
    this.statusFilter = 0;
  },
  methods: {
    isOpen(row) {
      const top = this.shell?.panelStack?.[0];
      return Boolean(top && top.key === `worker-${row.id}`);
    },
    /** Radnik za mesečni pregled (isti podaci kao stari dijalog: "more-restaurants-orders-by-month"). */
    toEmployeeContext(row) {
      const company = this.$store.state.company;
      return {
        id: row.id,
        name: row.name,
        lastname: "",
        fullName: row.name,
        companyId: company && company.id,
        companyName: this.toLatin((company && company.name) || ""),
        monthUrl: "/user/more-restaurants-orders-by-month",
      };
    },
    openWorker(row, event) {
      const employee = this.toEmployeeContext(row);
      this.$emit("open-panel", {
        opener: event?.currentTarget,
        key: `worker-${row.id}`,
        root: true,
        wide: true,
        title: row.name,
        subtitle: [`ID ${row.id}`, employee.companyName].filter(Boolean).join(" · "),
        component: ReportDesktopWorkerMonth,
        props: { compact: true, worker: employee },
      });
    },
    async copyTable() {
      const headers = [
        "ID",
        this.$t("worker"),
        ...(this.hasBranches ? [this.$t("branch")] : []),
        ...(this.hasExternalCode ? [this.$t("external_code")] : []),
        ...(this.isSingleDay ? [this.$t("time")] : []),
        ...this.visibleShifts.map((shift) => this.$t(shift.label)),
        this.$t("ordered"),
        this.$t("taken"),
        this.$t("difference"),
        this.$t("status"),
      ];
      const rows = this.rows.map((row) => [
        row.id,
        row.name,
        ...(this.hasBranches ? [row.companyName] : []),
        ...(this.hasExternalCode ? [row.externalCode] : []),
        ...(this.isSingleDay ? [row.time] : []),
        ...this.visibleShifts.map((shift) => row[shift.key]),
        row.ordered,
        row.taken,
        row.difference,
        row.statusLabel,
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
